import React, { useState } from 'react'
import { connect } from 'react-redux'
import { Button, Card, CardContent, Checkbox, CircularProgress, Typography, makeStyles } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'

import { CapeeshColors } from '../../assets/ColorPalette'
import { studentScreenMessages } from './StudentScreen.i18n.Messages'
import { studentService } from '../../_state/services'
import { studentActions, studentAuthActions } from '../../_state/actions'
import { capeeshWebAppVersion } from '../../Components/AppWebGL/StudentCapeeshWeb/CapeeshWebView'

const TERMS_URL = 'https://www.capeesh.com/about-us/privacy/'

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        justifyContent: 'center',
        padding: '40px 20px',
    },
    card: {
        maxWidth: '620px',
        width: '100%',
    },
    heading: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '20px',
        color: CapeeshColors.DarkText,
        marginBottom: '10px',
    },
    body: {
        fontFamily: 'Rubik',
        fontSize: '14px',
        color: CapeeshColors.Gray1,
        marginBottom: '20px',
    },
    termsRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '4px',
    },
    termsLabel: {
        fontFamily: 'Rubik',
        fontSize: '15px',
        color: CapeeshColors.DarkText,
    },
    termsLink: {
        color: CapeeshColors.Primary600,
        textDecoration: 'underline',
    },
    notice: {
        fontFamily: 'Rubik',
        fontSize: '13px',
        color: '#C62828',
        marginBottom: '8px',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginTop: '20px',
    },
}))

const StudentAcceptTerms = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    const { student, dispatch } = props

    const [accepted, setAccepted] = useState(false)
    const [showRequiredNotice, setShowRequiredNotice] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(null)

    const requiredVersion = student?.userInfo?.requiredTermsVersion

    const handleAccept = async () => {
        if (!accepted) {
            setShowRequiredNotice(true)
            return
        }

        setSubmitting(true)
        setError(null)
        try {
            await studentService.acceptTerms({ version: requiredVersion })
            // refresh the cached user info so the gate lets them through
            dispatch(studentActions.getStudentInfo(capeeshWebAppVersion))
        } catch (err) {
            console.error('Failed to accept privacy terms and conditions', err)
            setError(intl.formatMessage(studentScreenMessages.termsAcceptFailed))
            setSubmitting(false)
        }
    }

    const handleLogOut = () => {
        dispatch(studentAuthActions.studentLogout())
    }

    return (
        <div className={classes.root}>
            <Card className={classes.card}>
                <CardContent>
                    <Typography className={classes.heading}>
                        {intl.formatMessage(studentScreenMessages.termsGateHeading)}
                    </Typography>
                    <Typography className={classes.body}>
                        {intl.formatMessage(studentScreenMessages.termsGateBody)}
                    </Typography>

                    <div className={classes.termsRow}>
                        <Checkbox
                            checked={accepted}
                            onChange={(event) => {
                                setAccepted(event.target.checked)
                                if (event.target.checked) {
                                    setShowRequiredNotice(false)
                                }
                            }}
                            color="primary"
                            disabled={submitting}
                            inputProps={{ 'aria-label': 'accept privacy terms and conditions' }}
                        />
                        <Typography className={classes.termsLabel}>
                            <FormattedMessage id="student.screen.terms.accept.label" defaultMessage="I accept the" />{' '}
                            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className={classes.termsLink}>
                                <FormattedMessage
                                    id="student.screen.terms.accept.link.text"
                                    defaultMessage="privacy terms and conditions"
                                />
                            </a>
                        </Typography>
                    </div>

                    {showRequiredNotice && (
                        <Typography className={classes.notice}>
                            {intl.formatMessage(studentScreenMessages.termsRequiredHint)}
                        </Typography>
                    )}

                    {error && <Typography className={classes.notice}>{error}</Typography>}

                    <div className={classes.actions}>
                        <Button variant="contained" color="primary" onClick={handleAccept} disabled={submitting}>
                            {intl.formatMessage(studentScreenMessages.termsAcceptAndContinue)}
                        </Button>
                        {submitting && <CircularProgress size={22} />}
                        <Button onClick={handleLogOut} disabled={submitting}>
                            {intl.formatMessage(studentScreenMessages.termsLogOut)}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

const mapStateToProps = (state) => {
    const { student } = state
    return {
        student,
    }
}

export default connect(mapStateToProps)(StudentAcceptTerms)
